import { useCallback, useMemo, useRef, useState } from "react";
import type { CostAdjustment, CostAdjustmentEditable } from "../../../api_utils/types";
import { calculateCostAdjustmentAmount, formatCostAdjustmentAmount } from "./calculateCostAdjustmentAmount";

const formatClientSide = (costAdjustments: CostAdjustment[]): CostAdjustmentEditable[] =>
  costAdjustments.map((costAdjustment) => ({ ...costAdjustment, amount: costAdjustment.amount.toString() }));

export function useEditableCostAdjustments(initialCostAdjustments: CostAdjustment[], lineTotal: number): {
  costAdjustments: CostAdjustmentEditable[];
  setCostAdjustments: React.Dispatch<React.SetStateAction<CostAdjustmentEditable[]>>;
  total: number;
  attributes: CostAdjustment[];
  accept: (costAdjustments: CostAdjustment[]) => void;
  discard: () => void;
} {
  const [costAdjustments, setCostAdjustments] = useState(() => formatClientSide(initialCostAdjustments));
  const savedCostAdjustments = useRef(costAdjustments);
  const total = costAdjustments.reduce((sum, costAdjustment) => sum + calculateCostAdjustmentAmount(costAdjustment, lineTotal), 0);
  const attributes = useMemo(() => costAdjustments.map((costAdjustment) => ({
    ...costAdjustment, amount: formatCostAdjustmentAmount(costAdjustment),
  })), [costAdjustments]);

  const accept = useCallback((updatedCostAdjustments: CostAdjustment[]): void => {
    savedCostAdjustments.current = formatClientSide(updatedCostAdjustments);
    setCostAdjustments(savedCostAdjustments.current);
  }, []);
  const discard = useCallback((): void => { setCostAdjustments(savedCostAdjustments.current); }, []);

  return { costAdjustments, setCostAdjustments, total, attributes, accept, discard };
}
